<template>
  <TooltipProvider :delay-duration="200">
    <Head title="Degkalkylator" />
    <div class="w-full overflow-x-hidden">
      <!-- ── HERO ── -->
      <div
        class="relative overflow-hidden"
        style="height: clamp(300px, 45vw, 420px)"
      >
        <img
          :src="heroImg"
          alt="Degkalkylator"
          class="w-full h-full object-cover"
          style="object-position: center 30%"
        />
        <div
          class="absolute inset-0"
          style="
            background: linear-gradient(
              to top,
              rgba(0, 0, 0, 0.78) 0%,
              rgba(0, 0, 0, 0.3) 55%,
              rgba(0, 0, 0, 0) 100%
            );
          "
        />

        <Link
          href="/tekniker-och-verktyg"
          class="absolute top-5 left-4 md:top-7 md:left-8 flex items-center gap-1.5 hover:opacity-80 transition-opacity no-underline"
        >
          <div
            class="flex items-center gap-1.5 px-3 py-1.5 rounded-full"
            style="
              background-color: rgba(255, 255, 255, 0.15);
              backdrop-filter: blur(8px);
              border: 1px solid rgba(255, 255, 255, 0.2);
            "
          >
            <ArrowLeft :size="13" color="white" />
            <span
              class="font-[Poppins] font-bold text-[12px] text-white"
              style="letter-spacing: 0.5px"
              >Tekniker &amp; verktyg</span
            >
          </div>
        </Link>

        <div
          class="absolute bottom-0 left-0 right-0 max-w-[1280px] mx-auto px-4 md:px-8 pb-7 md:pb-10"
        >
          <div class="flex items-center gap-2 mb-3">
            <span
              class="inline-block px-3 py-1 rounded-full font-[Poppins] font-bold text-[10px] text-white"
              style="background-color: #14b8a6; letter-spacing: 1px"
              >VERKTYG</span
            >
            <span
              class="inline-block px-2.5 py-1 rounded-full font-[Poppins] font-bold text-[10px]"
              style="
                background-color: rgba(255, 255, 255, 0.15);
                backdrop-filter: blur(8px);
                border: 1px solid rgba(255, 255, 255, 0.3);
                color: rgba(255, 255, 255, 0.85);
                letter-spacing: 1px;
              "
              >BETA-VERSION</span
            >
          </div>
          <h1
            class="font-[Poppins] font-black text-white m-0 mb-2"
            style="
              font-size: clamp(26px, 5vw, 52px);
              letter-spacing: -1px;
              line-height: 1.15;
            "
          >
            Degkalkylator
          </h1>
          <p
            class="text-[15px] m-0"
            style="color: rgba(255, 255, 255, 0.75); max-width: 520px"
          >
            Beräkna rätt mängder för klassisk jästdeg och pizzadeg med
            bagarprocent. Detta är en beta-version av verktyget. Värden och
            resultat kan komma att justeras.
          </p>
        </div>
      </div>

      <!-- ── CALCULATOR ── -->
      <div class="max-w-[1280px] mx-auto px-4 md:px-8 py-8 md:py-12">
        <!-- Mode tabs -->
        <div
          class="flex gap-2 mb-8 p-1 rounded-2xl w-full sm:w-auto sm:inline-flex"
          style="background-color: #f0f2f4"
        >
          <button
            v-for="tab in MODES"
            :key="tab.id"
            :disabled="tab.disabled"
            class="flex-1 sm:flex-none flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 md:px-6 py-2.5 rounded-xl transition-all"
            :class="
              tab.disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'
            "
            :style="{
              border: 'none',
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 700,
              fontSize: '13px',
              backgroundColor: tab.id === mode ? 'white' : 'transparent',
              color: tab.id === mode ? '#191c1e' : '#6c7a77',
              boxShadow:
                tab.id === mode ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
            }"
            @click="!tab.disabled && (mode = tab.id)"
          >
            <span>{{ tab.emoji }}</span>
            <span class="hidden sm:inline">{{ tab.label }}</span>
            <span class="sm:hidden text-[11px]">{{
              tab.label.split(" ")[0]
            }}</span>
            <span
              v-if="tab.disabled"
              class="hidden sm:inline ml-1 text-[10px] opacity-60"
              >snart</span
            >
          </button>
        </div>

        <!-- Main 2-col grid -->
        <div class="flex flex-col lg:grid lg:grid-cols-2 lg:gap-8">
          <!-- ── INPUTS CARD ── -->
          <div class="mb-6 lg:mb-0">
            <div
              class="rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 h-full bg-white"
              style="border: 1.5px solid #eceef0"
            >
              <div class="flex items-center justify-between mb-6">
                <h2
                  class="font-[Poppins] font-bold text-[18px] m-0"
                  style="color: #191c1e"
                >
                  Ingredienser
                </h2>
                <button
                  class="flex items-center gap-1.5 hover:opacity-70 transition-opacity bg-transparent border-none cursor-pointer text-[13px]"
                  style="
                    color: #6c7a77;
                    font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                  "
                  @click="mode === 'pizza' ? handlePizzaReset() : handleReset()"
                >
                  <RotateCcw :size="13" />
                  Återställ
                </button>
              </div>

              <template v-if="mode === 'yeast'">
                <!-- Loaves -->
                <div class="mb-6">
                  <FieldLabel
                    tooltip="Multiplicerar alla ingredienser med antal bröd."
                    >Antal bröd / satser</FieldLabel
                  >
                  <div
                    class="flex items-center rounded-xl overflow-hidden"
                    style="
                      border: 1.5px solid rgba(20, 184, 166, 0.25);
                      background-color: white;
                    "
                  >
                    <button
                      class="stepper-btn"
                      @click="loaves = Math.max(1, loaves - 1)"
                    >
                      <ChevronDown :size="16" />
                    </button>
                    <input
                      v-model.number="loaves"
                      type="number"
                      min="1"
                      max="20"
                      class="flex-1 text-center bg-transparent outline-none border-none"
                      style="
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                        font-weight: 700;
                        font-size: 16px;
                        color: #191c1e;
                        width: 60px;
                      "
                    />
                    <button
                      class="stepper-btn"
                      @click="loaves = Math.min(20, loaves + 1)"
                    >
                      <ChevronUp :size="16" />
                    </button>
                  </div>
                </div>

                <!-- Flour -->
                <div class="mb-6">
                  <FieldLabel
                    tooltip="Mjölvikten är basen för alla bagarprocent-beräkningar."
                    >Mjöl (per sats)</FieldLabel
                  >
                  <div
                    class="flex items-center rounded-xl overflow-hidden"
                    style="
                      border: 1.5px solid rgba(20, 184, 166, 0.25);
                      background-color: white;
                    "
                  >
                    <button
                      class="stepper-btn"
                      @click="flour = Math.max(50, flour - 10)"
                    >
                      <ChevronDown :size="16" />
                    </button>
                    <input
                      v-model.number="flour"
                      type="number"
                      min="50"
                      max="5000"
                      step="10"
                      class="flex-1 text-center bg-transparent outline-none border-none"
                      style="
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                        font-weight: 700;
                        font-size: 16px;
                        color: #191c1e;
                        width: 60px;
                      "
                    />
                    <span
                      class="pr-2 text-[13px]"
                      style="
                        color: #6c7a77;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                      >g</span
                    >
                    <button
                      class="stepper-btn"
                      @click="flour = Math.min(5000, flour + 10)"
                    >
                      <ChevronUp :size="16" />
                    </button>
                  </div>
                  <p
                    class="mt-1.5 text-[11px] m-0"
                    style="
                      color: #a0b0ae;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                    "
                  >
                    Tips: 1 dl mjöl ≈ 60 g
                  </p>
                </div>

                <!-- Hydration -->
                <div class="mb-6">
                  <div class="flex items-center justify-between">
                    <FieldLabel
                      tooltip="Vattnets vikt i procent av mjölvikten. Högre hydrering = luftigare men klibbigare deg."
                      >Vatten</FieldLabel
                    >
                    <span
                      class="font-[Poppins] font-bold text-[15px]"
                      style="color: #14b8a6"
                      >{{ hydration }}%</span
                    >
                  </div>
                  <input
                    v-model.number="hydration"
                    type="range"
                    min="60"
                    max="100"
                    step="1"
                    class="dough-slider w-full mt-1"
                    :style="sliderBg(hydration, 60, 100)"
                  />
                  <div class="flex justify-between mt-1">
                    <span class="text-[11px]" style="color: #a0b0ae">60%</span>
                    <span class="text-[11px]" style="color: #a0b0ae">100%</span>
                  </div>
                </div>

                <!-- Salt -->
                <div class="mb-6">
                  <div class="flex items-center justify-between">
                    <FieldLabel
                      tooltip="Salt i procent av mjölvikten. Standard är 1.8–2.2%."
                      >Salt</FieldLabel
                    >
                    <span
                      class="font-[Poppins] font-bold text-[15px]"
                      style="color: #14b8a6"
                      >{{ salt }}%</span
                    >
                  </div>
                  <input
                    v-model.number="salt"
                    type="range"
                    min="0.5"
                    max="3"
                    step="0.1"
                    class="dough-slider w-full mt-1"
                    :style="sliderBg(salt, 0.5, 3)"
                  />
                  <div class="flex justify-between mt-1">
                    <span class="text-[11px]" style="color: #a0b0ae">0.5%</span>
                    <span class="text-[11px]" style="color: #a0b0ae">3.0%</span>
                  </div>
                </div>

                <!-- Yeast type toggle -->
                <div class="mb-5">
                  <p
                    class="font-[Poppins] font-bold text-[11px] uppercase m-0 mb-1.5"
                    style="color: #6c7a77; letter-spacing: 0.8px"
                  >
                    Jästtyp
                  </p>
                  <div class="flex gap-2">
                    <button
                      v-for="yt in YEAST_TYPES"
                      :key="yt.id"
                      class="flex-1 py-2 rounded-xl text-center transition-all cursor-pointer"
                      :style="{
                        border: `1.5px solid ${yeastType === yt.id ? '#14b8a6' : '#eceef0'}`,
                        backgroundColor:
                          yeastType === yt.id
                            ? 'rgba(20,184,166,0.06)'
                            : 'white',
                        fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        color: yeastType === yt.id ? '#14b8a6' : '#6c7a77',
                      }"
                      @click="setYeastType(yt.id)"
                    >
                      {{ yt.label }}
                    </button>
                  </div>
                </div>

                <!-- Rise speed presets -->
                <div class="mb-4">
                  <p
                    class="font-[Poppins] font-bold text-[11px] uppercase m-0 mb-1.5"
                    style="color: #6c7a77; letter-spacing: 0.8px"
                  >
                    Jäsningstid
                  </p>
                  <div class="flex gap-2">
                    <button
                      v-for="preset in RISE_PRESETS"
                      :key="preset.id"
                      class="flex-1 min-w-0 py-2 rounded-xl text-center transition-all cursor-pointer overflow-hidden"
                      :style="{
                        border: `1.5px solid ${riseSpeed === preset.id ? '#14b8a6' : '#eceef0'}`,
                        backgroundColor:
                          riseSpeed === preset.id
                            ? 'rgba(20,184,166,0.06)'
                            : 'white',
                        fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                        fontWeight: 700,
                        fontSize: '11px',
                        color: riseSpeed === preset.id ? '#14b8a6' : '#6c7a77',
                        whiteSpace: 'nowrap',
                      }"
                      @click="applyPreset(preset.id)"
                    >
                      {{ preset.emoji }} {{ preset.label }}
                    </button>
                  </div>
                  <Transition name="fade">
                    <p
                      v-if="activePreset"
                      class="mt-2 text-[12px] m-0"
                      style="
                        color: #6c7a77;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                    >
                      {{ activePreset.time }}
                    </p>
                  </Transition>
                </div>

                <!-- Torrjäst note -->
                <Transition name="fade">
                  <div
                    v-if="yeastType === 'dry'"
                    class="rounded-xl px-4 py-3 text-[13px] leading-relaxed"
                    style="
                      background-color: rgba(20, 184, 166, 0.06);
                      border: 1px solid rgba(20, 184, 166, 0.2);
                      color: #3c4947;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                    "
                  >
                    Torrjäst är mer koncentrerat än färsk jäst — mängderna ovan
                    är redan anpassade (ca halva mängden jämfört med färsk
                    jäst).
                  </div>
                </Transition>
              </template>
              <template v-else-if="mode === 'pizza'">
                <!-- Antal pizzor -->
                <div class="mb-6">
                  <FieldLabel tooltip="Antal pizzabollar du vill göra."
                    >Antal pizzor</FieldLabel
                  >
                  <div
                    class="flex items-center rounded-xl overflow-hidden"
                    style="
                      border: 1.5px solid rgba(20, 184, 166, 0.25);
                      background-color: white;
                    "
                  >
                    <button
                      class="stepper-btn"
                      @click="pizzaCount = Math.max(1, pizzaCount - 1)"
                    >
                      <ChevronDown :size="16" />
                    </button>
                    <input
                      v-model.number="pizzaCount"
                      type="number"
                      min="1"
                      max="20"
                      class="flex-1 text-center bg-transparent outline-none border-none"
                      style="
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                        font-weight: 700;
                        font-size: 16px;
                        color: #191c1e;
                        width: 60px;
                      "
                    />
                    <button
                      class="stepper-btn"
                      @click="pizzaCount = Math.min(20, pizzaCount + 1)"
                    >
                      <ChevronUp :size="16" />
                    </button>
                  </div>
                </div>

                <!-- Degvikt per pizza -->
                <div class="mb-6">
                  <FieldLabel
                    tooltip="Vikten på varje pizzaboll. Napolitansk pizza är typiskt 250–280 g."
                    >Degvikt per pizza</FieldLabel
                  >
                  <div
                    class="flex items-center rounded-xl overflow-hidden"
                    style="
                      border: 1.5px solid rgba(20, 184, 166, 0.25);
                      background-color: white;
                    "
                  >
                    <button
                      class="stepper-btn"
                      @click="
                        pizzaDoughWeight = Math.max(100, pizzaDoughWeight - 10)
                      "
                    >
                      <ChevronDown :size="16" />
                    </button>
                    <input
                      v-model.number="pizzaDoughWeight"
                      type="number"
                      min="100"
                      max="500"
                      step="10"
                      class="flex-1 text-center bg-transparent outline-none border-none"
                      style="
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                        font-weight: 700;
                        font-size: 16px;
                        color: #191c1e;
                        width: 60px;
                      "
                    />
                    <span
                      class="pr-2 text-[13px]"
                      style="
                        color: #6c7a77;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                      >g</span
                    >
                    <button
                      class="stepper-btn"
                      @click="
                        pizzaDoughWeight = Math.min(500, pizzaDoughWeight + 10)
                      "
                    >
                      <ChevronUp :size="16" />
                    </button>
                  </div>
                  <p
                    class="mt-1.5 text-[11px] m-0"
                    style="
                      color: #a0b0ae;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                    "
                  >
                    Napolitansk: 250–280 g · New York-stil: 300–350 g
                  </p>
                </div>

                <!-- Pizza vatten -->
                <div class="mb-6">
                  <div class="flex items-center justify-between">
                    <FieldLabel
                      tooltip="Vattnets vikt i procent av mjölvikten. Pizzadeg brukar ligga på 60–66%."
                      >Vatten</FieldLabel
                    >
                    <span
                      class="font-[Poppins] font-bold text-[15px]"
                      style="color: #14b8a6"
                      >{{ pizzaHydration }}%</span
                    >
                  </div>
                  <input
                    v-model.number="pizzaHydration"
                    type="range"
                    min="55"
                    max="80"
                    step="1"
                    class="dough-slider w-full mt-1"
                    :style="sliderBg(pizzaHydration, 55, 80)"
                  />
                  <div class="flex justify-between mt-1">
                    <span class="text-[11px]" style="color: #a0b0ae">55%</span>
                    <span class="text-[11px]" style="color: #a0b0ae">80%</span>
                  </div>
                </div>

                <!-- Pizza jästtyp -->
                <div class="mb-5">
                  <p
                    class="font-[Poppins] font-bold text-[11px] uppercase m-0 mb-1.5"
                    style="color: #6c7a77; letter-spacing: 0.8px"
                  >
                    Jästtyp
                  </p>
                  <div class="flex gap-2">
                    <button
                      v-for="yt in YEAST_TYPES"
                      :key="yt.id"
                      class="flex-1 py-2 rounded-xl text-center transition-all cursor-pointer"
                      :style="{
                        border: `1.5px solid ${pizzaYeastType === yt.id ? '#14b8a6' : '#eceef0'}`,
                        backgroundColor:
                          pizzaYeastType === yt.id
                            ? 'rgba(20,184,166,0.06)'
                            : 'white',
                        fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                        fontWeight: 700,
                        fontSize: '12px',
                        color: pizzaYeastType === yt.id ? '#14b8a6' : '#6c7a77',
                      }"
                      @click="setPizzaYeastType(yt.id)"
                    >
                      {{ yt.label }}
                    </button>
                  </div>
                </div>

                <!-- Pizza jäsningstid -->
                <div class="mb-4">
                  <p
                    class="font-[Poppins] font-bold text-[11px] uppercase m-0 mb-1.5"
                    style="color: #6c7a77; letter-spacing: 0.8px"
                  >
                    Jäsningstid
                  </p>
                  <div class="flex gap-2">
                    <button
                      v-for="preset in RISE_PRESETS"
                      :key="preset.id"
                      class="flex-1 min-w-0 py-2 rounded-xl text-center transition-all cursor-pointer overflow-hidden"
                      :style="{
                        border: `1.5px solid ${pizzaRiseSpeed === preset.id ? '#14b8a6' : '#eceef0'}`,
                        backgroundColor:
                          pizzaRiseSpeed === preset.id
                            ? 'rgba(20,184,166,0.06)'
                            : 'white',
                        fontFamily: '\'Plus Jakarta Sans\', sans-serif',
                        fontWeight: 700,
                        fontSize: '11px',
                        color:
                          pizzaRiseSpeed === preset.id ? '#14b8a6' : '#6c7a77',
                        whiteSpace: 'nowrap',
                      }"
                      @click="pizzaApplyPreset(preset.id)"
                    >
                      {{ preset.emoji }} {{ preset.label }}
                    </button>
                  </div>
                  <Transition name="fade">
                    <p
                      v-if="pizzaActivePreset"
                      class="mt-2 text-[12px] m-0"
                      style="
                        color: #6c7a77;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                    >
                      {{ pizzaActivePreset.time }}
                    </p>
                  </Transition>
                </div>

                <!-- Pizza torrjäst note -->
                <Transition name="fade">
                  <div
                    v-if="pizzaYeastType === 'dry'"
                    class="rounded-xl px-4 py-3 text-[13px] leading-relaxed"
                    style="
                      background-color: rgba(20, 184, 166, 0.06);
                      border: 1px solid rgba(20, 184, 166, 0.2);
                      color: #3c4947;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                    "
                  >
                    Torrjäst är mer koncentrerat än färsk jäst — mängderna ovan
                    är redan anpassade (ca halva mängden jämfört med färsk
                    jäst).
                  </div>
                </Transition>
              </template>
            </div>
          </div>

          <!-- ── RESULTS CARD ── -->
          <div>
            <div
              class="rounded-2xl md:rounded-3xl p-4 sm:p-6 md:p-8 bg-white"
              style="
                border: 2px solid rgba(20, 184, 166, 0.2);
                box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
              "
            >
              <h2
                class="font-[Poppins] font-bold text-[18px] m-0 mb-5"
                style="color: #191c1e"
              >
                Resultat
              </h2>

              <!-- Ingredient rows -->
              <div
                v-for="row in activeIngredientRows"
                :key="row.label"
                class="flex items-center justify-between py-3 border-b"
                style="border-color: #f0f2f4"
              >
                <div class="flex items-center gap-2.5">
                  <span
                    class="rounded-full shrink-0"
                    :style="{
                      width: '10px',
                      height: '10px',
                      backgroundColor: row.color,
                    }"
                  />
                  <span
                    class="text-[15px]"
                    style="
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      color: #3c4947;
                    "
                    >{{ row.label }}</span
                  >
                </div>
                <div class="flex items-center gap-3 sm:gap-4">
                  <span
                    class="text-[13px] text-right tabular-nums"
                    style="
                      color: #a0b0ae;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      min-width: 44px;
                    "
                    >{{ row.pct.toFixed(1) }}%</span
                  >
                  <Transition name="flip" mode="out-in">
                    <span
                      :key="row.grams"
                      class="font-[Poppins] font-bold text-[15px] text-right tabular-nums"
                      style="color: #191c1e; min-width: 54px"
                      >{{ row.grams.toFixed(0) }} g</span
                    >
                  </Transition>
                  <span
                    class="text-[12px] text-right tabular-nums"
                    style="
                      color: #a0b0ae;
                      font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      min-width: 72px;
                    "
                    >{{ row.measure ?? "" }}</span
                  >
                </div>
              </div>

              <!-- Total row -->
              <div class="flex items-center justify-between py-3">
                <span
                  class="text-[15px] font-bold"
                  style="
                    font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                    color: #191c1e;
                  "
                  >{{ activeTotalLabel }}</span
                >
                <Transition name="flip" mode="out-in">
                  <span
                    :key="activeTotalGrams"
                    class="font-[Poppins] font-bold text-[15px] text-right"
                    style="color: #14b8a6; min-width: 60px"
                    >{{ activeTotalGrams.toFixed(0) }} g</span
                  >
                </Transition>
              </div>

              <!-- Bar chart -->
              <div class="mt-6 pt-5 border-t" style="border-color: #f0f2f4">
                <p
                  class="font-[Poppins] font-bold text-[11px] m-0 mb-3 uppercase"
                  style="color: #6c7a77; letter-spacing: 0.8px"
                >
                  Fördelning
                </p>
                <div
                  class="flex overflow-hidden rounded-full"
                  style="height: 10px"
                >
                  <div
                    v-for="bar in activeBarItems"
                    :key="bar.label"
                    :style="{
                      width: `${(bar.value / activeTotalGrams) * 100}%`,
                      backgroundColor: bar.color,
                      transition: 'width 0.3s ease',
                    }"
                  />
                </div>
                <div class="flex flex-wrap gap-x-4 gap-y-1.5 mt-3">
                  <div
                    v-for="bar in activeBarItems"
                    :key="bar.label"
                    class="flex items-center gap-1.5"
                  >
                    <span
                      class="rounded-full shrink-0"
                      :style="{
                        width: '8px',
                        height: '8px',
                        backgroundColor: bar.color,
                      }"
                    />
                    <span
                      class="text-[12px]"
                      style="
                        color: #6c7a77;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                    >
                      {{ bar.label }}
                      {{ ((bar.value / activeTotalGrams) * 100).toFixed(0) }}%
                    </span>
                  </div>
                </div>
              </div>

              <!-- Per pizza note (pizza mode only) -->
              <div
                v-if="mode === 'pizza'"
                class="mt-5 rounded-xl px-4 py-3 text-[13px] leading-relaxed"
                style="
                  background-color: rgba(20, 184, 166, 0.04);
                  border: 1px solid rgba(20, 184, 166, 0.15);
                  color: #3c4947;
                  font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                "
              >
                Per pizza ({{ pizzaDoughWeight }}g degboll): mjöl
                {{ (pizzaFlourGrams / pizzaCount).toFixed(0) }}g · vatten
                {{ (pizzaWaterGrams / pizzaCount).toFixed(0) }}g · salt
                {{ (pizzaSaltGrams / pizzaCount).toFixed(0) }}g · jäst
                {{ (pizzaYeastGrams / pizzaCount).toFixed(0) }}g
              </div>

              <!-- Baker's % note (yeast mode only) -->
              <p
                v-if="mode === 'yeast'"
                class="mt-5 text-[12px] leading-relaxed m-0"
                style="
                  color: #a0b0ae;
                  font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                "
              >
                Procentsatserna baseras på
                <strong>mjölets vikt = 100%</strong> (bagarprocent). Alla andra
                ingredienser anges i relation till mjölet.
              </p>

              <!-- Hydration context box -->
              <Transition name="fade" mode="out-in">
                <div
                  :key="activeHydrationContext.title"
                  class="flex gap-4 rounded-2xl p-5 mt-5"
                  style="
                    background-color: rgba(20, 184, 166, 0.06);
                    border: 1px solid rgba(20, 184, 166, 0.18);
                  "
                >
                  <Lightbulb :size="20" class="text-teal-500 shrink-0 mt-0.5" />
                  <div class="flex flex-col gap-1.5">
                    <p
                      class="font-[Poppins] font-bold text-[14px] text-teal-500 m-0"
                    >
                      {{ activeHydrationContext.title }}
                    </p>
                    <p
                      class="text-[15px] leading-[1.75] m-0"
                      style="
                        color: #3c4947;
                        font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                      "
                    >
                      {{ activeHydrationContext.text }}
                    </p>
                  </div>
                </div>
              </Transition>
            </div>
          </div>
        </div>

        <!-- ── TIPS ── -->
        <div class="mt-8 md:mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div
            v-for="tip in TIPS"
            :key="tip.title"
            class="rounded-2xl p-5 md:p-6"
            style="
              background-color: rgba(20, 184, 166, 0.05);
              border: 1px solid rgba(20, 184, 166, 0.12);
            "
          >
            <span style="font-size: 24px">{{ tip.emoji }}</span>
            <h3
              class="font-[Poppins] font-bold text-[15px] mt-2.5 mb-1.5 m-0"
              style="color: #191c1e"
            >
              {{ tip.title }}
            </h3>
            <p
              class="text-[14px] leading-[1.65] m-0"
              style="
                color: #3c4947;
                font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
              "
            >
              {{ tip.text }}
            </p>
          </div>
        </div>

        <!-- ── CTA STRIP ── -->
        <div
          class="mt-8 md:mt-10 rounded-[24px] md:rounded-[32px] overflow-hidden"
          style="background-color: #f0fdf9"
        >
          <div
            class="px-6 md:px-10 py-7 md:py-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:gap-10"
          >
            <div class="flex-1">
              <p
                class="font-[Poppins] font-bold m-0 mb-1"
                style="font-size: clamp(15px, 2vw, 18px); color: #191c1e"
              >
                Vill du lära dig mer om surdeg?
              </p>
              <p
                class="text-[14px] m-0"
                style="
                  color: #3c4947;
                  font-family: &quot;Plus Jakarta Sans&quot;, sans-serif;
                "
              >
                Läs Emmas guide om surdegsbakning.
              </p>
            </div>
            <Link
              href="/tekniker-och-verktyg/surdegsbrod-101"
              class="inline-flex self-start sm:self-auto items-center gap-2 px-5 py-3 rounded-xl hover:opacity-90 transition-opacity shrink-0 no-underline"
              style="background-color: #14b8a6"
            >
              <span class="font-[Poppins] font-bold text-[14px] text-white"
                >Surdegsguiden →</span
              >
            </Link>
          </div>
        </div>
      </div>
    </div>
  </TooltipProvider>
</template>

<script setup>
import { ref, computed } from "vue";
import { Head, Link } from "@inertiajs/vue3";
import {
  ArrowLeft,
  ChevronDown,
  ChevronUp,
  Info,
  Lightbulb,
  RotateCcw,
} from "lucide-vue-next";
import heroImg from "@/images/dough-calculator-hero.jpg";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// ── Sub-component: label with optional tooltip ──────────────
const FieldLabel = {
  props: { tooltip: String },
  components: { Tooltip, TooltipContent, TooltipTrigger, Info },
  setup() {
    const open = ref(false);
    function onPointerEnter(e) {
      if (e.pointerType === "mouse") open.value = true;
    }
    function onPointerLeave(e) {
      if (e.pointerType === "mouse") open.value = false;
    }
    function onClick(e) {
      e.stopPropagation();
      open.value = !open.value;
    }
    return { open, onPointerEnter, onPointerLeave, onClick };
  },
  template: `
    <div class="flex items-center gap-1.5 mb-1.5">
      <span style="font-family: 'Poppins', sans-serif; font-weight: 700; font-size: 12px; color: #6c7a77; letter-spacing: 0.8px; text-transform: uppercase">
        <slot />
      </span>
      <Tooltip v-if="tooltip" :open="open" @update:open="open = $event">
        <TooltipTrigger as-child>
          <span
            class="cursor-pointer inline-flex items-center justify-center -m-2 p-2 rounded-full"
            @pointerenter="onPointerEnter"
            @pointerleave="onPointerLeave"
            @click="onClick"
          ><Info :size="15" color="#a0b0ae" /></span>
        </TooltipTrigger>
        <TooltipContent class="max-w-[200px] text-[12px] leading-relaxed">
          {{ tooltip }}
        </TooltipContent>
      </Tooltip>
    </div>
  `,
};

// ── Constants ────────────────────────────────────────────────
const MODES = [
  { id: "yeast", label: "Klassisk deg", emoji: "🍞", disabled: false },
  { id: "pizza", label: "Pizza-deg", emoji: "🍕", disabled: false },
  { id: "sourdough", label: "Surdeg", emoji: "🌾", disabled: true },
  { id: "enriched", label: "Berikad deg", emoji: "🧈", disabled: true },
];

const YEAST_TYPES = [
  { id: "fresh", label: "Färsk jäst" },
  { id: "dry", label: "Torrjäst" },
];

const RISE_SPEED_DATA = {
  snabb: {
    emoji: "🏃",
    label: "Snabb",
    fresh: 7,
    dry: 2.3,
    time: "Jäser på 1–1,5 timme",
  },
  langsom: {
    emoji: "🕐",
    label: "Långsam",
    fresh: 3.5,
    dry: 1.2,
    time: "Jäser på 2–4 timmar",
  },
  kall: {
    emoji: "❄️",
    label: "Kalljäsning",
    fresh: 1.2,
    dry: 0.4,
    time: "Kylskåpsjäsning 8–12 timmar",
  },
};

const RISE_PRESETS = [
  { id: "snabb", emoji: "🏃", label: "Snabb" },
  { id: "langsom", emoji: "🕐", label: "Långsam" },
  { id: "kall", emoji: "❄️", label: "Kalljäsning" },
];

const TIPS = [
  {
    emoji: "🌡️",
    title: "Temperatur spelar roll",
    text: "Vattnets temperatur påverkar jästiden kraftigt. Sikta på 24–28°C för normal jäsning. Kallt vatten = långsammare, varmt = snabbare.",
  },
  {
    emoji: "⚖️",
    title: "Väg alltid",
    text: "För absolut bästa resultat, använd alltid en våg när du måttar mjöl. Om du måttar med decilitermått kan din deg få fel konsistens eftersom mjölet packas olika i måttet varje gång du tar en ny skopa.",
  },
  {
    emoji: "📝",
    title: "Anteckna dina satser",
    text: "Skriv ned exakt vad du använde. Nästa gång du bakar vet du precis vad som funkade – och vad du vill justera.",
  },
];

const DEFAULTS = {
  flour: 500,
  loaves: 1,
  hydration: 65,
  salt: 2.0,
  yeastType: "fresh",
};
const PIZZA_DEFAULTS = {
  count: 4,
  doughWeight: 250,
  hydration: 65,
  yeastType: "dry",
};

// ── State ────────────────────────────────────────────────────
const mode = ref("yeast");
const flour = ref(DEFAULTS.flour);
const loaves = ref(DEFAULTS.loaves);
const hydration = ref(DEFAULTS.hydration);
const salt = ref(DEFAULTS.salt);
const yeastPct = ref(RISE_SPEED_DATA.snabb.fresh);
const yeastType = ref(DEFAULTS.yeastType);
const riseSpeed = ref("snabb");

// ── Pizza state ──────────────────────────────────────────────
const pizzaCount = ref(PIZZA_DEFAULTS.count);
const pizzaDoughWeight = ref(PIZZA_DEFAULTS.doughWeight);
const pizzaHydration = ref(PIZZA_DEFAULTS.hydration);
const pizzaYeastPct = ref(RISE_SPEED_DATA.langsom.dry);
const pizzaYeastType = ref(PIZZA_DEFAULTS.yeastType);
const pizzaRiseSpeed = ref("langsom");

// ── Computed: label ──────────────────────────────────────────
const yeastLabel = computed(() =>
  yeastType.value === "fresh" ? "Färsk jäst" : "Torrjäst",
);

// ── Computed: results ────────────────────────────────────────
const flourGrams = computed(() => flour.value * loaves.value);
const waterGrams = computed(() =>
  Math.round(flourGrams.value * (hydration.value / 100)),
);
const saltGrams = computed(() =>
  Math.round(flourGrams.value * (salt.value / 100)),
);
const yeastGrams = computed(() =>
  Math.round(flourGrams.value * (yeastPct.value / 100)),
);
const totalGrams = computed(
  () =>
    flourGrams.value + waterGrams.value + saltGrams.value + yeastGrams.value,
);

const ingredientRows = computed(() => [
  {
    label: "Mjöl",
    grams: flourGrams.value,
    pct: 100,
    color: "#f59e0b",
    measure: `ca ${fmtMeasure(flourGrams.value / 60)} dl`,
  },
  {
    label: "Vatten",
    grams: waterGrams.value,
    pct: hydration.value,
    color: "#0ea5e9",
    measure: `ca ${fmtMeasure(waterGrams.value / 100)} dl`,
  },
  {
    label: "Salt",
    grams: saltGrams.value,
    pct: salt.value,
    color: "#94a3b8",
    measure: `ca ${saltVolume(saltGrams.value)}`,
  },
  {
    label: yeastLabel.value,
    grams: yeastGrams.value,
    pct: yeastPct.value,
    color: "#a78bfa",
    measure:
      yeastType.value === "fresh"
        ? "-"
        : `ca ${fmtMeasure(yeastGrams.value / 3)} tsk`,
  },
]);

const barItems = computed(() => [
  { label: "Mjöl", value: flourGrams.value, color: "#f59e0b" },
  { label: "Vatten", value: waterGrams.value, color: "#0ea5e9" },
  { label: "Salt", value: saltGrams.value, color: "#94a3b8" },
  { label: "Jäst", value: yeastGrams.value, color: "#a78bfa" },
]);

// ── Computed: hydration context ─────────────────────────────
const hydrationContext = computed(() => {
  const h = hydration.value;
  if (h <= 65)
    return {
      title: "Klassisk deg — baguette och frallor",
      text: `${h}% hydrering är standard för klassiska degar. Degen är smidig och lätt att forma — perfekt för baguetter, frallor och de flesta vardagsbröd.`,
    };
  if (h <= 74)
    return {
      title: "Lätt klibbig — rustika limpor",
      text: `${h}% vatten ger en mjuk, lätt klibbig deg. Passar för rustika limpor och lantbröd. Kräver mjuk hantering — undvik att knåda för hårt.`,
    };
  if (h <= 80)
    return {
      title: "Hög hydrering — ciabatta och surdeg",
      text: `${h}% hydrering är typiskt för ciabatta (75–80%) och många surdegsbröd. Degen är lös och kladdig — forma med mjölade händer och använd stretch-and-fold istället för knådning.`,
    };
  return {
    title: "Mycket hög hydrering — focaccia",
    text: `${h}% vatten ger en nästan flytande deg — klassiskt för focaccia (80–100%). Degen knådas inte alls; häll den i en oljad form, vik ett par gånger och låt jäsningen göra jobbet. Resultatet: luftig smula och knaprig yta.`,
  };
});

// ── Computed: active preset ───────────────────────────────────
const activePreset = computed(() =>
  riseSpeed.value ? RISE_SPEED_DATA[riseSpeed.value] : null,
);

// ── Pizza computeds ─────────────────────────────────────────
const PIZZA_SALT_PCT_OF_TOTAL = 1.4;
const pizzaTotalDough = computed(
  () => pizzaCount.value * pizzaDoughWeight.value,
);
const pizzaSaltGrams = computed(() =>
  Math.round((pizzaTotalDough.value * PIZZA_SALT_PCT_OF_TOTAL) / 100),
);
const pizzaFlourGrams = computed(() => {
  const remaining = pizzaTotalDough.value - pizzaSaltGrams.value;
  const factor = 1 + pizzaHydration.value / 100 + pizzaYeastPct.value / 100;
  return Math.round(remaining / factor);
});
const pizzaWaterGrams = computed(() =>
  Math.round((pizzaFlourGrams.value * pizzaHydration.value) / 100),
);
const pizzaYeastGrams = computed(() =>
  Math.round((pizzaFlourGrams.value * pizzaYeastPct.value) / 100),
);
const pizzaYeastLabel = computed(() =>
  pizzaYeastType.value === "fresh" ? "Färsk jäst" : "Torrjäst",
);
const pizzaSaltBakersPct = computed(
  () => (pizzaSaltGrams.value / pizzaFlourGrams.value) * 100,
);

const pizzaIngredientRows = computed(() => [
  { label: "Mjöl", grams: pizzaFlourGrams.value, pct: 100, color: "#f59e0b" },
  {
    label: "Vatten",
    grams: pizzaWaterGrams.value,
    pct: pizzaHydration.value,
    color: "#0ea5e9",
  },
  {
    label: "Salt",
    grams: pizzaSaltGrams.value,
    pct: pizzaSaltBakersPct.value,
    color: "#94a3b8",
  },
  {
    label: pizzaYeastLabel.value,
    grams: pizzaYeastGrams.value,
    pct: pizzaYeastPct.value,
    color: "#a78bfa",
  },
]);

const pizzaBarItems = computed(() => [
  { label: "Mjöl", value: pizzaFlourGrams.value, color: "#f59e0b" },
  { label: "Vatten", value: pizzaWaterGrams.value, color: "#0ea5e9" },
  { label: "Salt", value: pizzaSaltGrams.value, color: "#94a3b8" },
  { label: "Jäst", value: pizzaYeastGrams.value, color: "#a78bfa" },
]);

const pizzaActivePreset = computed(() =>
  pizzaRiseSpeed.value ? RISE_SPEED_DATA[pizzaRiseSpeed.value] : null,
);

const pizzaHydrationContext = computed(() => {
  const h = pizzaHydration.value;
  if (h <= 62)
    return {
      title: "Fast pizzadeg — lätt att forma",
      text: `${h}% vatten ger en fast, hanterbar deg. Passar perfekt för tunnare pizzabottnar och nybörjare.`,
    };
  if (h <= 66)
    return {
      title: "Klassisk napolitansk — luftig och mjuk",
      text: `${h}% är standardhydrering för napolitansk pizza. Degen är smidig med bra stretch och ger en luftig, fläckig kant.`,
    };
  if (h <= 72)
    return {
      title: "New York-stil — seg och tuggig",
      text: `${h}% vatten ger en degigare och mer seg pizza. Populär för storbottnar och New York-pizza. Lite klibbigare att hantera.`,
    };
  return {
    title: "Hög hydrering — avancerad teknik",
    text: `${h}% är en väldigt blöt pizzadeg. Kräver lång kylskåpsjäsning och van hantering, men ger en extremt luftig botten.`,
  };
});

// ── Active (mode-aware) computeds ────────────────────────────
const activeIngredientRows = computed(() =>
  mode.value === "pizza" ? pizzaIngredientRows.value : ingredientRows.value,
);
const activeTotalGrams = computed(() =>
  mode.value === "pizza" ? pizzaTotalDough.value : totalGrams.value,
);
const activeBarItems = computed(() =>
  mode.value === "pizza" ? pizzaBarItems.value : barItems.value,
);
const activeHydrationContext = computed(() =>
  mode.value === "pizza" ? pizzaHydrationContext.value : hydrationContext.value,
);
const activeTotalLabel = computed(() => {
  if (mode.value === "pizza")
    return `Totalt — ${pizzaCount.value} ${pizzaCount.value === 1 ? "pizza" : "pizzor"}`;
  return "Totalt";
});

// ── Helpers ──────────────────────────────────────────────────
function roundHalf(n) {
  return Math.round(n * 2) / 2;
}
function fmtMeasure(n) {
  return String(roundHalf(n)).replace(".", ",");
}
function saltVolume(g) {
  const tsk = roundHalf(g / 5);
  if (tsk >= 3) return `${fmtMeasure(g / 15)} msk`;
  return `${fmtMeasure(g / 5)} tsk`;
}

function sliderBg(value, min, max) {
  const pct = ((value - min) / (max - min)) * 100;
  return {
    background: `linear-gradient(to right, #14b8a6 ${pct}%, #e6e8ea ${pct}%)`,
  };
}

function handleReset() {
  flour.value = DEFAULTS.flour;
  loaves.value = DEFAULTS.loaves;
  hydration.value = DEFAULTS.hydration;
  salt.value = DEFAULTS.salt;
  yeastType.value = DEFAULTS.yeastType;
  applyPreset("snabb");
}

function applyPreset(id) {
  riseSpeed.value = id;
  yeastPct.value = +RISE_SPEED_DATA[id][yeastType.value].toFixed(2);
}

function onYeastSliderChange() {
  riseSpeed.value = null;
}

function setYeastType(type) {
  yeastType.value = type;
  if (riseSpeed.value) {
    yeastPct.value = +RISE_SPEED_DATA[riseSpeed.value][type].toFixed(2);
  }
}

function handlePizzaReset() {
  pizzaCount.value = PIZZA_DEFAULTS.count;
  pizzaDoughWeight.value = PIZZA_DEFAULTS.doughWeight;
  pizzaHydration.value = PIZZA_DEFAULTS.hydration;

  pizzaYeastType.value = PIZZA_DEFAULTS.yeastType;
  pizzaApplyPreset("snabb");
}

function pizzaApplyPreset(id) {
  pizzaRiseSpeed.value = id;
  pizzaYeastPct.value = +RISE_SPEED_DATA[id][pizzaYeastType.value].toFixed(2);
}

function setPizzaYeastType(type) {
  pizzaYeastType.value = type;
  if (pizzaRiseSpeed.value) {
    pizzaYeastPct.value =
      +RISE_SPEED_DATA[pizzaRiseSpeed.value][type].toFixed(2);
  }
}
</script>

<style scoped>
/* Slider track & thumb */
.dough-slider {
  -webkit-appearance: none;
  appearance: none;
  height: 6px;
  border-radius: 3px;
  outline: none;
  cursor: pointer;
}
.dough-slider::-webkit-slider-thumb {
  -webkit-appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #14b8a6;
  border: 2px solid white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}
.dough-slider::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #14b8a6;
  border: 2px solid white;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.2);
  cursor: pointer;
}

/* Stepper buttons */
.stepper-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 42px;
  flex-shrink: 0;
  background: none;
  border: none;
  cursor: pointer;
  color: #14b8a6;
  transition: background-color 0.15s;
}
.stepper-btn:hover {
  background-color: rgba(20, 184, 166, 0.08);
}

/* Flip animation for result numbers */
.flip-enter-active,
.flip-leave-active {
  transition:
    opacity 0.12s ease,
    transform 0.12s ease;
}
.flip-enter-from {
  opacity: 0;
  transform: translateY(-5px);
}
.flip-leave-to {
  opacity: 0;
  transform: translateY(5px);
}

/* Fade animation for conditional elements */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
